import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTrashRepos, useTrashWorkspaces } from "../api/hooks/useTrash";
import { useRestoreFromTrash, usePurgeFromTrash } from "./useTrashActions";
import { slugErrorKind } from "./slugError";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Table, THead, TBody, Tr, Th, Td } from "../ui/Table";
import { SkeletonRows } from "../ui/Skeleton";
import { useToast } from "../ui/Toast";
import { useTranslation, apiErrorMessage } from "../i18n";
import type { ApiError } from "../api/client";
import type { TrashEntry } from "../api/types";

const DAY_MS = 86_400_000;

/** Whole days left before the retention sweep purges the entry (never < 0). */
function daysLeft(purgeAt: string, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(purgeAt) - now) / DAY_MS));
}

// ── One row ──────────────────────────────────────────────────────────────────

function TrashRow({
  entry,
  wsSlug,
  now,
}: {
  readonly entry: TrashEntry;
  readonly wsSlug: string;
  readonly now: number;
}) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const toast = useToast();
  const restore = useRestoreFromTrash(wsSlug);
  const purge = usePurgeFromTrash(wsSlug);
  // Permanent delete is irreversible, so the row's button arms first and only
  // the second click fires it (the confirm surface a destructive action needs).
  const [armed, setArmed] = useState(false);

  function restoreFailure(err: ApiError): string {
    // The two restore-specific failures are actionable in different ways: a
    // live row already holds the name, or the plan slot was refilled.
    if (slugErrorKind(err) === "taken") return t("trash.restoreConflict", { name: entry.name });
    // A slug that was legal when the workspace was trashed can be reserved by
    // the time it is restored — the root namespace grew a static route under
    // it. Unlike a live collision, renaming something else does not help.
    if (slugErrorKind(err) === "reserved") return t("trash.restoreReserved");
    if (err.code === "upgrade_required") return t("trash.restoreQuota");
    return apiErrorMessage(err, tErr);
  }

  const busy = restore.isPending || purge.isPending;

  return (
    <Tr data-testid={`trash-row-${entry.id}`}>
      <Td className="text-slate-400">{t(`trash.type.${entry.type}`)}</Td>
      <Td className="font-medium text-slate-100">{entry.name}</Td>
      <Td className="text-slate-400">{new Date(entry.deletedAt).toLocaleDateString()}</Td>
      <Td className="text-slate-400">{t("trash.daysLeft", { days: daysLeft(entry.purgeAt, now) })}</Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() =>
              restore.mutate(entry, {
                onSuccess: () => toast.success(t("trash.restored", { name: entry.name })),
                onError: (err) => toast.error(restoreFailure(err)),
              })
            }
          >
            {t("trash.restore")}
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={busy}
            onClick={() => {
              if (!armed) {
                setArmed(true);
                return;
              }
              purge.mutate(entry, {
                onSuccess: () => toast.success(t("trash.purged", { name: entry.name })),
                onError: (err) => toast.error(apiErrorMessage(err, tErr)),
              });
            }}
          >
            {armed ? t("trash.deleteConfirm") : t("trash.delete")}
          </Button>
        </div>
      </Td>
    </Tr>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function TrashPage() {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const { slug } = useParams<{ slug: string }>();
  const wsSlug = slug ?? "";
  const trashedWorkspaces = useTrashWorkspaces();
  const trashedRepos = useTrashRepos(wsSlug);

  // One list, newest delete first — the bin is chronological, not per-type.
  const entries = useMemo(() => {
    const all = [...(trashedWorkspaces.data ?? []), ...(trashedRepos.data ?? [])];
    return all.sort((a, b) => Date.parse(b.deletedAt) - Date.parse(a.deletedAt));
  }, [trashedWorkspaces.data, trashedRepos.data]);

  // Fixed per render pass: every row's countdown reads the same instant.
  const now = Date.now();
  const loading = trashedWorkspaces.isLoading || trashedRepos.isLoading;
  const error = trashedWorkspaces.error ?? trashedRepos.error;

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900 p-6">
      <h2 className="mb-1 text-lg font-semibold text-slate-100">{t("trash.title")}</h2>
      <p className="mb-4 text-sm text-slate-400">{t("trash.description")}</p>

      {error ? (
        <Banner kind="error" className="mb-4">{apiErrorMessage(error, tErr)}</Banner>
      ) : null}

      {loading ? (
        <SkeletonRows rows={3} />
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-400">{t("trash.empty")}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-700">
          <Table>
            <THead>
              <Tr>
                <Th>{t("trash.colType")}</Th>
                <Th>{t("trash.colName")}</Th>
                <Th>{t("trash.colDeleted")}</Th>
                <Th>{t("trash.colRetention")}</Th>
                <Th className="w-48" />
              </Tr>
            </THead>
            <TBody>
              {entries.map((entry) => (
                <TrashRow key={`${entry.type}:${entry.id}`} entry={entry} wsSlug={wsSlug} now={now} />
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </section>
  );
}
